import { useEffect, useMemo, useRef, useState } from "react";
import { tmApi, type TmUserOption } from "../../api/tm";

type Props = {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  disabled?: boolean;
  autoFocus?: boolean;
};

/**
 * Textarea with @mention autocomplete. Triggers on "@" preceded by start of
 * input or whitespace; popup lists Bankom-internal users by handle (email local
 * part) + full name; arrow keys + Enter / click insert "@handle " back into
 * the textarea at the cursor. The BE TmMentionExtractor parses the same
 * "@handle" syntax to fan out notifications.
 */
export function MentionTextarea({
  value, onChange, placeholder, rows = 3, maxLength = 5000, disabled, autoFocus,
}: Props) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [users, setUsers] = useState<TmUserOption[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [anchor, setAnchor] = useState<{ start: number; end: number } | null>(null);

  useEffect(() => {
    void tmApi.users().then(setUsers).catch(() => setUsers([]));
  }, []);

  const matches = useMemo(() => {
    if (!open) return [];
    const q = query.toLowerCase();
    const scored = users.map((u) => {
      const handle = (u.email.split("@")[0] || "").toLowerCase();
      const name = u.name.toLowerCase();
      return { u, handle, score: q === "" ? 0 : (handle.startsWith(q) ? 0 : handle.includes(q) ? 1 : name.includes(q) ? 2 : 3) };
    }).filter((m) => q === "" || m.score < 3);
    scored.sort((a, b) => a.score - b.score);
    return scored.slice(0, 8);
  }, [open, query, users]);

  function detect() {
    const ta = taRef.current;
    if (!ta) return;
    const pos = ta.selectionStart ?? 0;
    const left = value.slice(0, pos);
    const m = /(^|\s)@([A-Za-z0-9._-]*)$/.exec(left);
    if (!m) {
      if (open) setOpen(false);
      setAnchor(null);
      return;
    }
    const startAt = pos - (m[2].length + 1);
    setAnchor({ start: startAt, end: pos });
    setQuery(m[2] ?? "");
    setOpen(true);
    setActiveIdx(0);
  }

  function pick(u: TmUserOption) {
    const ta = taRef.current;
    if (!ta || !anchor) return;
    const handle = u.email.split("@")[0] ?? u.email;
    const before = value.slice(0, anchor.start);
    const after = value.slice(anchor.end);
    const inserted = `@${handle} `;
    const next = before + inserted + after;
    onChange(next);
    setOpen(false);
    setAnchor(null);
    const newPos = anchor.start + inserted.length;
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(newPos, newPos);
    }, 0);
  }

  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!open || matches.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      pick(matches[activeIdx].u);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div style={{ position: "relative" }}>
      <textarea
        ref={taRef}
        className="input"
        value={value}
        onChange={(e) => { onChange(e.target.value); }}
        onKeyUp={detect}
        onClick={detect}
        onKeyDown={onKey}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        rows={rows}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        style={{ width: "100%", boxSizing: "border-box" }}
      />
      {open && matches.length > 0 && (
        <div
          role="listbox"
          className="dd-scroll"
          style={{
            position: "absolute", bottom: "calc(100% + 4px)", left: 0,
            minWidth: 240, background: "var(--surface)",
            border: "1px solid var(--border)", borderRadius: 8,
            boxShadow: "var(--shadow-2)",
            padding: 4, zIndex: 30, maxHeight: 240, overflowY: "auto",
          }}
        >
          {matches.map(({ u, handle }, i) => (
            <button
              key={u.id}
              type="button"
              role="option"
              aria-selected={i === activeIdx}
              onMouseDown={(e) => { e.preventDefault(); pick(u); }}
              onMouseEnter={() => setActiveIdx(i)}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                width: "100%", textAlign: "left",
                padding: "6px 8px", borderRadius: 6,
                background: i === activeIdx ? "var(--surface-2)" : "transparent",
                border: "none", cursor: "pointer", fontSize: 13,
              }}
            >
              <span style={{
                width: 22, height: 22, borderRadius: "50%",
                background: "var(--brand-primary)", color: "var(--on-primary)",
                display: "grid", placeItems: "center",
                fontSize: 10, fontWeight: 700, flex: "none",
              }}>{u.name.split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}</span>
              <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.25, minWidth: 0 }}>
                <strong style={{ color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.name}</strong>
                <span style={{ color: "var(--ink-3)", fontSize: 11 }}>@{handle}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
